/* eslint-disable class-methods-use-this */
import { EMPTY_STRING } from "@regrello/core-utils";
import type {
  ConditionOperator,
  FieldFields,
  FieldInstanceFields,
  FieldInstanceFieldsWithBaseValues,
  FieldSpectrumFields,
  FieldType,
  FormConstraintConditionOperator,
  FormFieldFields,
  Maybe,
  PropertyDataType,
  PropertyTypeFields,
  RegrelloObjectPropertyFields,
  SpectrumFieldConstraintFields,
  SpectrumFieldValidationTypeFields,
  SpectrumFieldVersionFields,
  SpectrumValueConstraintFields,
  TableFieldInstanceFields,
  UpdateFieldInstanceValueInputs,
  ViewFilterFields,
} from "@regrello/graphql-api";
import type { RegrelloIconProps } from "@regrello/ui-core";
import type { ColDef, ValueFormatterParams } from "ag-grid-community";
import type { ReactNode } from "react";
import type { FieldArrayWithId, FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import type {
  FieldInstanceBaseFields,
  FieldInstanceValueBaseTabularValueInputs,
  FieldInstanceValueBaseValueInputs,
} from "../../../../../types";
import type { ConfigureSpectrumFieldFormFormFields } from "../../../../../ui2/fields/configure/_internal/ConfigureSpectrumFieldForm";
import type { RowDatum } from "../../../../../ui2/forms/instance/utils/tableV2Utils";
import { consoleWarnInDevelopmentModeOnly } from "../../../../../utils/environmentUtils";
import type { PartyTypeUnion } from "../../../../../utils/parties/PartyTypeUnion";
import type { AiAgentSettings } from "../../customFields/plugins/types/AiAgentSettings";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  GetCreateFieldInstanceValueInputsFromFormValueParams,
  ValidateBulkUploadCellValueOptions,
  WithOwnerData,
} from "../../customFields/plugins/types/CustomFieldPlugin";
import type { ValidationResult } from "../../customFields/plugins/utils/validateBulkUploadCellValueUtils";
import { getActiveRuleAsBoolean } from "../utils/spectrumFieldConstraintUtils";
import type { ProspectiveSpectrumFieldVersion } from "./ProspectiveSpectrumFieldVersion";

export type SpectrumFormUpdateMode = "onBlur" | "onChange";

/**
 * A type for an array of `SpectrumFieldPluginDecorator`s.
 */
// The `SpectrumFieldPluginDecorator` interface is designed to be generic, allowing each plugin to
// specify its own value type (boolean, string, number, etc.). The registrar needs to store all
// these different plugin types in a single array. There are some members in the
// `SpectrumFieldPluginDecorator` interface that return specific types. `unknown` is insufficient
// for this and results in type errors when defining plugins within this array. Thus, we use `any`
// instead.
//
// biome-ignore lint/suspicious/noExplicitAny: For reasoning, see above.
export type SpectrumFieldPluginDecorators = Array<SpectrumFieldPluginDecorator<any>>;

export abstract class SpectrumFieldPluginDecorator<TFrontendValue> implements CustomFieldPlugin<TFrontendValue> {
  public getAiAgentSettings?: () => AiAgentSettings;
  public isCreateAndEditAllowed: boolean;
  public uri: string;
  public version: string;

  private customFieldPlugin: CustomFieldPlugin<TFrontendValue>;

  constructor(plugin: CustomFieldPlugin<TFrontendValue>) {
    this.customFieldPlugin = plugin;
    this.getAiAgentSettings = plugin.getAiAgentSettings;
    this.uri = plugin.uri;
    this.version = plugin.version;
    this.isCreateAndEditAllowed = plugin.isCreateAndEditAllowed;
  }

  public getSourceFieldInstanceInputType(fieldInstance: FieldInstanceFields | FieldInstanceBaseFields) {
    return this.customFieldPlugin.getSourceFieldInstanceInputType(fieldInstance);
  }

  public canProcessPropertyDataType(propertyDataType: PropertyDataType) {
    return this.customFieldPlugin.canProcessPropertyDataType(propertyDataType);
  }

  public canProcessField(field: FieldFields) {
    return this.customFieldPlugin.canProcessField(field);
  }

  public canProcessFieldInstance(fieldInstance: FieldInstanceBaseFields) {
    // (clewis): It's particularly important to use canProcessSpectrumField to distinguish fields that
    // share a legacy property type.
    return this.canProcessSpectrumField(fieldInstance.spectrumFieldVersion);
  }

  public findPropertyTypeIdFromLoadedPropertyTypeIds(loadedPropertyTypes: PropertyTypeFields[]) {
    return this.customFieldPlugin.findPropertyTypeIdFromLoadedPropertyTypeIds(loadedPropertyTypes);
  }

  public getCreateFieldInstanceValueInputsFromFormValue(inputs: GetCreateFieldInstanceValueInputsFromFormValueParams) {
    return this.customFieldPlugin.getCreateFieldInstanceValueInputsFromFormValue(inputs);
  }

  public getColumnsForTable(field: FieldSpectrumFields) {
    return this.customFieldPlugin.getColumnsForTable(field);
  }

  public getCrossWorkflowSinksFieldInstanceIds(fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues) {
    return this.customFieldPlugin.getCrossWorkflowSinksFieldInstanceIds(fieldInstance);
  }

  public getCrossWorkflowSourceFieldInstanceIdFromValue(
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ) {
    return this.customFieldPlugin.getCrossWorkflowSourceFieldInstanceIdFromValue(fieldInstance);
  }

  public getSinkFieldInstanceIds(fieldInstance: FieldInstanceFields | FieldInstanceBaseFields) {
    return this.customFieldPlugin.getSinkFieldInstanceIds(fieldInstance);
  }

  public getFieldDisplayName() {
    return this.customFieldPlugin.getFieldDisplayName();
  }

  public getEmptyValueForFrontend(options?: { operator: ConditionOperator }, defaultValue?: string) {
    return this.customFieldPlugin.getEmptyValueForFrontend(options, defaultValue);
  }

  public getEmptyTabularValueForFrontend(rowNumber: number) {
    return this.customFieldPlugin.getEmptyTabularValueForFrontend(rowNumber);
  }

  public getFieldProperties(field: FieldFields) {
    return this.customFieldPlugin.getFieldProperties?.(field);
  }

  public getFilterDefinition(field: FieldFields) {
    return this.customFieldPlugin.getFilterDefinition(field);
  }

  public getFilterDefinitionWithValues(field: FieldFields, filter: ViewFilterFields, parties: PartyTypeUnion[]) {
    return this.customFieldPlugin.getFilterDefinitionWithValues(field, filter, parties);
  }

  public getHelperText() {
    if (this.customFieldPlugin.getHelperText == null) {
      consoleWarnInDevelopmentModeOnly("getHelperText is not undefined by CustomFieldPlugin");
    }
    return this.customFieldPlugin.getHelperText?.() ?? EMPTY_STRING;
  }

  public getIconName(fieldType?: FieldType, field?: FieldFields) {
    return this.customFieldPlugin.getIconName(fieldType, field);
  }

  public getNameTemplateDisplayValueFromFormValue(
    value: unknown,
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
    options?: { regrelloObjectProperty?: RegrelloObjectPropertyFields },
  ) {
    return this.customFieldPlugin.getNameTemplateDisplayValueFromFormValue(value, fieldInstance, options);
  }

  public getPreferredHomeTableColumnWidth() {
    if (this.customFieldPlugin.getPreferredHomeTableColumnWidth == null) {
      consoleWarnInDevelopmentModeOnly("getPreferredHomeTableColumnWidth is not undefined by CustomFieldPlugin");
    }
    return this.customFieldPlugin.getPreferredHomeTableColumnWidth?.() ?? 100;
  }

  public getPreferredMultilinePoColumnWidth() {
    if (this.customFieldPlugin.getPreferredMultilinePoColumnWidth == null) {
      consoleWarnInDevelopmentModeOnly("getPreferredHomeTableColumnWidth is not undefined by CustomFieldPlugin");
    }
    return this.customFieldPlugin.getPreferredMultilinePoColumnWidth?.() ?? 100;
  }

  public getSourceFieldInstance(fieldInstance: FieldInstanceFields) {
    return this.customFieldPlugin.getSourceFieldInstance(fieldInstance);
  }

  public getSourceFieldInstanceId(fieldInstance: FieldInstanceBaseFields) {
    return this.customFieldPlugin.getSourceFieldInstanceId(fieldInstance);
  }

  public getConditionOperators() {
    return this.customFieldPlugin.getConditionOperators();
  }

  public getUpdateStartingConditionsInputsFromFormValues(
    leftFieldInstance: FieldInstanceFields,
    value: unknown,
    operator: ConditionOperator,
    leftFieldInstancePropertyId?: Maybe<number>,
  ) {
    return this.customFieldPlugin.getUpdateStartingConditionsInputsFromFormValues(
      leftFieldInstance,
      value,
      operator,
      leftFieldInstancePropertyId,
    );
  }

  public getUpdateFieldInstanceValueInputsFromFieldInstance(
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ) {
    return this.customFieldPlugin.getUpdateFieldInstanceValueInputsFromFieldInstance(fieldInstance);
  }

  public getValueForFrontend(
    fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
    options?: { includeInactiveValues: boolean },
  ) {
    return this.customFieldPlugin.getValueForFrontend(fieldInstance, options);
  }

  public getTabularValueForFrontend(fieldInstance: TableFieldInstanceFields) {
    return this.customFieldPlugin.getTabularValueForFrontend(fieldInstance);
  }

  public hasAllowedValues() {
    return this.customFieldPlugin.hasAllowedValues?.() ?? false;
  }

  public hasFieldUnit() {
    return this.customFieldPlugin.hasFieldUnit?.() ?? false;
  }

  public isFeatureFlagEnabled() {
    return this.customFieldPlugin.isFeatureFlagEnabled();
  }

  public isFieldInstanceEmpty(
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
    options?: { includeInactiveOptions: boolean },
  ) {
    return this.customFieldPlugin.isFieldInstanceEmpty(fieldInstance, options);
  }

  public isFieldInstanceValueEqual(
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
    fieldInstance2: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ) {
    return this.customFieldPlugin.isFieldInstanceValueEqual(fieldInstance, fieldInstance2);
  }

  public isFieldInstanceValueUnchanged(
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
    proposedChange: FieldInstanceValueBaseValueInputs,
  ) {
    return this.customFieldPlugin.isFieldInstanceValueUnchanged(fieldInstance, proposedChange);
  }

  public isFieldInstanceTabularValueUnchanged(
    fieldInstance: TableFieldInstanceFields,
    proposedChange: FieldInstanceValueBaseTabularValueInputs,
  ) {
    return this.customFieldPlugin.isFieldInstanceTabularValueUnchanged(fieldInstance, proposedChange);
  }

  public canBeRequired() {
    return this.customFieldPlugin.canBeRequired?.() ?? true;
  }

  public isSpectrumFormFieldValueRequired(fieldInstance: FieldInstanceFields) {
    return getActiveRuleAsBoolean(fieldInstance.spectrumMetadata ?? undefined).required;
  }

  public isMultiValued() {
    return this.customFieldPlugin.isMultiValued?.() ?? false;
  }

  public isNeedsFieldUnit() {
    return this.customFieldPlugin.isNeedsFieldUnit?.() ?? false;
  }

  /**
   * Parses a tabular value that may have been pasted from a clipboard, imported via CSV upload, or
   * keyed in manually via an editable cell input.
   *
   * For example:
   * - may convert the string "true" to the boolean `true` for checkbox fields.
   * - may convert an allowed value string to a proper allowed value consisting of {id,value}.
   *
   * @returns The parsed value with a type guaranteed to be compatible with this field plugin.
   */
  // __Note:__ This is necessary to define because `RegrelloDataGridV2` does not invoke a column
  // definition's `valueParser` on bulk pastes. This function can be used within `valueParser`; in
  // fact this is probably a good practice so we don't have .
  public parseTableValue(_fieldVersion: SpectrumFieldVersionFields, valueIn: unknown): TFrontendValue {
    return valueIn as TFrontendValue;
  }

  public renderDisplayValue(
    fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
    options?: {
      context?: "default" | "table" | "tableV2" | "inlineWrappable" | "inlineTruncate" | "dataTab";
      regrelloObjectProperty?: Maybe<RegrelloObjectPropertyFields>;
      maxLines?: -1 | 1 | 2 | 3 | 4 | 5 | 10;
    },
  ) {
    return this.customFieldPlugin.renderDisplayValue(fieldInstance, options);
  }

  public renderDisplayValueFromStringValue(
    fieldVersion: SpectrumFieldVersionFields,
    value: string,
    options?: Parameters<NonNullable<CustomFieldPlugin<TFrontendValue>["renderDisplayValueFromStringValue"]>>[2],
  ) {
    return this.customFieldPlugin.renderDisplayValueFromStringValue?.(fieldVersion, value, options);
  }

  public renderFormField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    fieldFields: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
    options?: { context?: "RegrelloActionItemViewRequestedInfo" | "RegrelloConfigureCustomFieldsFormSection" },
  ) {
    return this.customFieldPlugin.renderFormField(fieldFields, props, options);
  }

  /**
   * Returns the icon for this field type.
   */
  public renderIcon(props?: Omit<RegrelloIconProps, "iconName"> & { fieldType?: FieldType; field?: FieldFields }) {
    return this.customFieldPlugin.renderIcon(props);
  }

  public renderMultipleDisplayValuesForDataGrid(
    fieldInstances:
      | Array<FieldInstanceBaseFields & WithOwnerData>
      | Array<FieldInstanceFields | FieldInstanceFieldsWithBaseValues>,
    options?: { context: "table" | "tableV2"; regrelloObjectProperty?: Maybe<RegrelloObjectPropertyFields> },
  ) {
    return this.customFieldPlugin.renderMultipleDisplayValuesForDataGrid(fieldInstances, options);
  }

  public sortComparator(
    fieldInstance1: FieldInstanceBaseFields | undefined,
    fieldInstance2: FieldInstanceBaseFields | undefined,
    direction?: "asc" | "desc",
    options?: {
      regrelloObjectProperty?: Maybe<RegrelloObjectPropertyFields>;
    },
  ) {
    return this.customFieldPlugin.sortComparator(fieldInstance1, fieldInstance2, direction, options);
  }

  /**
   * Validates a user-provided cell value within a bulk-start-workflows CSV. Returns a
   * `ValidationResult` object that can be used to render the cell value and validation result in
   * the UI.
   *
   * All SpectrumPlugins must override this definition to call the legacy field Plugins' validate
   * followed by regular Spectrum field validation to ensure Spectrum-specific field constraints
   * are applied.
   *
   * @param field - the legacy field for the data being parsed
   * @param value - the input value from the uploaded CSV document
   * @param options - additional options for validation.
   */
  public abstract validateBulkUploadCellValue(
    field: FieldFields,
    value: string,
    options: ValidateBulkUploadCellValueOptions,
  ): ValidationResult;

  /**
   * Returns `true` if this plugin should be used to process the `SpectrumValidationType` provided.
   * Multiple plugins may be able to process the same `PropertyDataType`. If you want a funciton
   * that only returns one unique `CustomFieldPlugin`, use the `canProcessField` or
   * `canProcessFieldInstance` functions.
   */
  public abstract canProcessValidationType(spectrumValidationType: SpectrumFieldValidationTypeFields): boolean;

  /**
   * Returns `true` if this plugin should be used to process the provided spectrum field, which will
   * have came from the backend via GraphQL, or `false` otherwise.
   *
   * ___Warning:___ Each field returned from the backend must be processable by _exactly one_
   * custom-field plugin on the frontend; otherwise, an error will be thrown by the plugin framework
   * when that field is rendered in any capacity.
   *
   * ___Warning:___ This logic must match the logic in `visitors/visitFieldVersion.ts`.
   *
   * ___Therefore:___ When adding a new custom-field plugin, please check diligently that the set of
   * fields it can process is disjoint from the set of fields that any other plugin can process.
   */
  public abstract canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion): boolean;

  /**
   * Given an array of all property types (loaded from the backend via GraphQL), finds and returns
   * the property type that is compatible with this type of custom field.
   *
   * This can be used to provide the appropriate `propertyType` to the backend when the user
   * creates a field.
   */
  public abstract findPropertyTypeFromLoadedPropertyTypes(
    propertyTypes: PropertyTypeFields[],
  ): PropertyTypeFields | undefined;

  /**
   * Given an array of all property types (loaded from the backend via GraphQL), finds and returns
   * the property type that is compatible with this type of custom field.
   *
   * This can be used to provide the appropriate `propertyType` to the backend when the user
   * creates a field.
   */
  public abstract findValidationTypeFromLoadedValidationTypes(
    validationTypes: SpectrumFieldValidationTypeFields[],
  ): SpectrumFieldValidationTypeFields | undefined;

  /**
   * Given an array of all property types (loaded from the backend via GraphQL), finds and returns
   * the property type that is compatible with this type of custom field.
   *
   * This can be used to provide the appropriate `propertyType` to the backend when the user creates
   * a field.
   *
   * @param valueConstraints - The set of all value constraints in Regrello to filter.
   * @param fieldPropertyDataType - The property data type of an existing field being edited, if
   * editing.
   * @returns The set of value constraints that are compatible with the provided property data type,
   * or with this plugin if the {@link fieldPropertyDataType} is undefined (e.g., if creating).
   */
  public abstract findValueConstraintsFromLoadedValueConstraints(
    valueConstraints: SpectrumValueConstraintFields[],
    fieldPropertyDataType: PropertyDataType | undefined,
  ): SpectrumValueConstraintFields[];

  /**
   * Returns the configuration for when an async update should be triggered. `onBlur` is applicable
   * to most field types. "onChange" should be used instead when the field is rendered as a complex
   * component and that the `onBlur` event isn't a good indicator. For example: a checkbox, a date
   * picker or a signature.
   */
  public abstract getSpectrumFormAutosaveMode(): SpectrumFormUpdateMode;

  /**
   * Returns supported `FormConstraintConditionOperator` map.
   */
  public abstract getConstraintConditionOperators(
    field: SpectrumFieldVersionFields,
  ): Partial<Record<FormConstraintConditionOperator, { label: string; inputCount: number; isMultiselect: boolean }>>;

  /**
   * Retrieves the list of constraints to be displayed for this field version.
   * While this is most often just an array of all the constraints that are defined on the field version, there
   * are some cases where a constraint that isn't defined should still be rendered. This can happen if the null/
   * default value is still useful; i.e. if no "disableSeparators" is set, the default is to enable separators.
   */
  public abstract getConstraintsToDisplay(field: SpectrumFieldVersionFields): SpectrumFieldConstraintFields[];

  public abstract renderSpectrumFormField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode;

  public abstract renderPreviewFormField(spectrumField: SpectrumFieldVersionFields): React.ReactNode;

  /**
   * Renders the value constraint input form fields appropriate for the plugin type.
   */
  public abstract renderValueConstraints(props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;

    /**
     * Prevent having multiple values for this field, if the field supports multiple values at all.
     *
     * - If provided, then the field can only have a single value when instantiated and will show a
     *   tooltip with this explanation text on hover of the multiplicity switch.
     * - If undefined and the field supports multiplicity, the field will be allowed to have multiple
     *   values when instantiated.
     * - Will be ignored if the field does not innately support multiple values.
     */
    suppressMultiplicityWithExplanation?: string;

    /**
     * If the frontend value of the value constraint field isn't a string, you can pass
     * in an update function to achieve the conversion
     *
     * @param index - the index of the field in it's immediate parent field array
     * @param value - the value to update with
     */
    updateConstraint?: (index: number, value: ConfigureSpectrumFieldFormFormFields["valueConstraints"][number]) => void;
  }): React.ReactNode;

  /**
   * Returns the column definitions of the field instance that will be used to render the tabular
   * value in AGGrid.
   */
  public abstract getColumnDef<TData extends RowDatum>(
    colDefParams: Partial<ColDef<TData, TFrontendValue>>,
    options: {
      fieldInstance: FieldInstanceFields;
      formField?: FormFieldFields;
      placeholderRenderer?: (params: ValueFormatterParams<TData, TFrontendValue>) => ReactNode;

      /**
       * The user's preferred text wrapping behavior in text-type cells.
       *
       * - `clip`: The text will be clipped to one line, truncating if it is longer than the cell width.
       * - `wrap`: The text will wrap to multiple lines.
       *
       * @default "wrap"
       */
      textFormat?: "clip" | "wrap";
    },
  ): ColDef<TData, TFrontendValue>;

  public abstract getFieldUpdateInputsFromFrontendValue(
    fieldInstance: FieldInstanceFields,
    formValue: TFrontendValue,
  ): UpdateFieldInstanceValueInputs;

  /**
   * Check whether a field version is allowed as a Form Table column.
   *
   * @param fieldVersion - The field version being added to a Form Table
   * @returns true if this field version is supported as a Form Table column.
   */
  public abstract isValidTableColumn(fieldVersion: SpectrumFieldVersionFields): boolean;

  /**
   * Validates a value against the field's constraints and type-specific rules.
   *
   * @param spectrumFieldVersion - The field version containing constraints.
   * @param value - The value to validate.
   * @param isRequired - Whether the field is required. This is typically managed by the field
   * instance's `spectrumMetadata`.
   * @returns Validation result with `isValid` flag and optional error message.
   */
  public abstract validateValue(
    fieldVersion: SpectrumFieldVersionFields,
    value: unknown,
    isRequired: boolean,
  ): { isValid: boolean; errors: string[] };
}
